import http from './http';
import { ROUTES } from './routes';
import type {
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  StageGoalsProgress,
  GoalItem
} from '../types/project';

// 认证相关API
export const auth = {
  register: async (data: { username?: string; email: string; password: string }) => {
    const response = await http.post(ROUTES.AUTH.REGISTER, data);
    return response.data;
  },

  resendVerification: async (email: string, password: string) => {
    const response = await http.post(ROUTES.AUTH.RESEND_VERIFICATION, { email, password });
    return response.data;
  },

  login: async (username: string, password: string) => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    const response = await http.post(ROUTES.AUTH.LOGIN, params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await http.post(ROUTES.AUTH.ME);
    return response.data;
  },

  updateProfile: async (data: {
    email?: string;
    currentPassword: string;
    newPassword?: string;
  }) => {
    const response = await http.post(ROUTES.AUTH.UPDATE_PROFILE, data);
    return response.data;
  },

  updateAvatar: async (formData: FormData) => {
    const response = await http.post(ROUTES.AUTH.UPDATE_AVATAR, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAvatar: async (filename: string) => {
    const response = await http.get(ROUTES.AUTH.AVATAR(filename), {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  },

  logout: () => {
    // 清除所有认证相关的存储
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    // 清除其他可能的认证状态
    sessionStorage.clear();
    // 清除所有 cookie
    document.cookie.split(';').forEach(cookie => {
      document.cookie = cookie.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
  },

  forgotPassword: async (email: string) => {
    const response = await http.post(ROUTES.AUTH.FORGOT_PASSWORD, { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await http.post(ROUTES.AUTH.RESET_PASSWORD, {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await http.post(ROUTES.AUTH.VERIFY_EMAIL, { token });
    return response.data;
  },

  addTrialMembership: async () => {
    const response = await http.post(ROUTES.AUTH.ADD_TRIAL_MEMBERSHIP);
    return response.data;
  },
};

// 项目相关API
export const projects = {
  getAll: async (): Promise<Project[]> => {
    const response = await http.get(ROUTES.PROJECTS.BASE);
    return response.data;
  },

  create: async (data: ProjectCreateRequest): Promise<Project> => {
    try {
      const response = await http.post(ROUTES.PROJECTS.CREATE, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.detail || '创建项目失败，请检查输入');
      }
      throw error;
    }
  },

  update: async (id: string, data: ProjectUpdateRequest): Promise<Project> => {
    const response = await http.patch(ROUTES.PROJECTS.DETAIL(id), data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await http.delete(ROUTES.PROJECTS.DELETE(id));
  },
};

// 工具相关API
export const tools = {
  recordClick: async (data: { tool_name: string; tool_url: string }) => {
    const response = await http.post('/tools/click', {
      ...data,
      ip_address: '0.0.0.0'
    });
    return response.data;
  }
}; 

// 聊天相关API
export const chat = {
  send: async (message: string, projectId: string, stage: number) => {
    const response = await http.post(ROUTES.CHAT.SEND, {
      message,
      projectId,
      stage
    });
    return response.data;
  },

  getHistory: async (projectId: string, stage?: number) => {
    const url = ROUTES.CHAT.HISTORY(projectId);
    const response = await http.get(stage !== undefined ? `${url}?stage=${stage}` : url);
    return response.data;
  },

  archive: async (chatId: string) => {
    const response = await http.post(ROUTES.CHAT.ARCHIVE(chatId));
    return response.data;
  }
}; 

// 阶段目标相关API
export const stageGoals = {
  getAll: async (projectId: string): Promise<StageGoalsProgress[]> => {
    const response = await http.get(ROUTES.STAGE_GOALS.GET_ALL(projectId));
    return response.data;
  },

  get: async (projectId: string, stage: number): Promise<StageGoalsProgress> => {
    const response = await http.get(ROUTES.STAGE_GOALS.GET(projectId, stage));
    return response.data;
  },

  create: async (projectId: string, stage: number, goals: GoalItem[]): Promise<StageGoalsProgress> => {
    const response = await http.post(ROUTES.STAGE_GOALS.GET_ALL(projectId), {
      stage,
      goals
    });
    return response.data;
  },

  update: async (projectId: string, stage: number, goals: GoalItem[]): Promise<StageGoalsProgress> => {
    const response = await http.patch(ROUTES.STAGE_GOALS.UPDATE(projectId, stage), {
      goals
    });
    return response.data;
  }
}; 